/**
 * MissionDataEntry — Per-project wrapper around MetricEntryGrid for Mission sub-KPI data entry.
 *
 * This component:
 * 1. Derives MetricRowDefs from the project's mission subKPI results
 * 2. Calls the createMissionMetricPoint API on save
 * 3. Notifies the host to refresh data after successful writes
 *
 * Designed for inline placement on the Mission tab within Performance.
 */

import React, { useCallback, useMemo } from "react";
import MetricEntryGrid, {
  type MetricRowDef,
  type MetricEntrySavePayload,
} from "./MetricEntryGrid";
import MetricEntryStacked from "./MetricEntryStacked";
import {
  createMissionMetricPoint,
  updateMissionMetricPoint,
  fetchMissionMetricPointsByProject,
} from "../../api/missionMetrics";
import { ApiError } from "../../api/client";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { invalidateAndRefetch, QUERY_KEYS } from "../../api/queryCache";
// Seed data import removed — targets come exclusively from the API via props.

// ── Helpers ─────────────────────────────────────────────────

/** Convert snake_case or camelCase subKpiKey to a human-readable label. */
function titleize(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Types ───────────────────────────────────────────────────

export interface MissionDataEntryProps {
  /** Project GUID */
  projectGuid: string;
  /** Mission subKPI results from the scoring engine (selectedItem.mission_subkpis) */
  missionSubkpis: any[];
  /** API-provided or seed targets for this project */
  projectTargets?: Record<string, any>;
  /** Whether the current user can edit (canEditProjectContent) */
  canEdit: boolean;
  /** Called after a successful save so the host can refresh data. Receives
   *  save coverage so the host can tell a FULL save from a PARTIAL one — the
   *  Action Center only resolves the data item on full coverage (DEV-1256). */
  onDataSaved?: (info?: { savedCount: number; totalCount: number }) => void;
  /** Layout variant: "grid" (wide, default) or "stacked" (narrow tray). */
  variant?: "grid" | "stacked";
}

// ── Component ───────────────────────────────────────────────

const MissionDataEntry: React.FC<MissionDataEntryProps> = ({
  projectGuid,
  missionSubkpis,
  projectTargets,
  canEdit,
  onDataSaved,
  variant = "grid",
}) => {
  const { t } = useAppTranslation("kpi");

  // Resolve targets from API-provided data (no seed data fallback).
  const targets = useMemo(() => {
    return projectTargets ?? {};
  }, [projectTargets]);

  // (Removed 2026-05-11 per Skip — diagnostic log fired per render.)

  // Build metric row definitions from subKPI results
  const metrics: MetricRowDef[] = useMemo(() => {
    if (!Array.isArray(missionSubkpis) || missionSubkpis.length === 0) {
      return [];
    }

    return missionSubkpis.map((sk) => {
      const key = String(sk?.subKpiKey ?? "");
      const target = targets?.[key];
      const label = sk?.label || titleize(key);

      // Resolve value type from scoring engine output
      const rawVt = String(sk?.value_type ?? "numeric");
      const valueType = (rawVt === "binary" || rawVt === "percentage" || rawVt === "milestone_set")
        ? rawVt as "binary" | "percentage" | "milestone_set"
        : "numeric" as const;

      return {
        key,
        label,
        unit: target?.unit ?? sk?.uom ?? undefined,
        currentValue: sk?.latest_value ?? null,
        targetValue: target?.goal ?? sk?.target_goal ?? null,
        direction: sk?.direction ?? target?.direction ?? undefined,
        valueType,
        milestoneTotal: target?.milestone_total ?? sk?.milestone_total ?? undefined,
      };
    });
  }, [missionSubkpis, targets]);

  // Save handler — upsert: try POST, if 409 Conflict then find existing point and PUT.
  const handleSave = useCallback(
    async (entries: MetricEntrySavePayload[]) => {
      // Save sequentially to avoid race conditions on the backend
      for (const entry of entries) {
        try {
          await createMissionMetricPoint({
            projectId: projectGuid,
            subKpiKey: entry.key,
            reportingDate: entry.reportingDate,
            value: entry.value,
            note: entry.note || undefined,
          });
        } catch (err: any) {
          // 409 Conflict = point already exists for this date — update instead
          if (err instanceof ApiError && err.status === 409) {
            const existing = await fetchMissionMetricPointsByProject(
              projectGuid,
              entry.key
            );
            const match = existing.find(
              (p) => p.reportingDate === entry.reportingDate
            );
            if (match) {
              await updateMissionMetricPoint(match.id, {
                value: entry.value,
                note: entry.note || undefined,
              });
            } else {
              // Unexpected: 409 but no matching point found — rethrow
              throw err;
            }
          } else {
            throw err;
          }
        }
      }

      // Invalidate the mission query cache so useApiMissionData re-fetches
      // immediately rather than waiting for the projectConfigVersion round-trip.
      // Use invalidateAndRefetch — a raw dispatchEvent doesn't mark the
      // cache entry stale, so subscribers receive the event but fetchForKey
      // returns the cached (stale) value.
      invalidateAndRefetch(QUERY_KEYS.mission);

      // Notify host to refresh (bumps projectConfigVersion → triggers re-fetch)
      if (onDataSaved) {
        onDataSaved({ savedCount: entries.length, totalCount: metrics.length });
      }
    },
    [projectGuid, onDataSaved, metrics.length]
  );

  if (metrics.length === 0 || !canEdit) {
    return null; // Don't render if no mission subKPIs configured or user lacks edit permission
  }

  const Comp = variant === "stacked" ? MetricEntryStacked : MetricEntryGrid;
  return (
    <Comp
      metrics={metrics}
      onSave={handleSave}
      readOnly={!canEdit}
      title={t("dataEntry.missionTitle") || "Enter Mission Data"}
    />
  );
};

export default MissionDataEntry;
